import { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useQuery } from 'react-query'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Box, Button, Grid, Paper, Stack, Typography } from '@mui/material'

import { AppLayoutContext } from '../../providers/AppLayout'
import { AppStateContext } from '../../providers/AppStateContext'

import { AppPermissions } from '../../models/AppPermissions.model'
import UserService from '../../services/UserService/UserService'
import { TrailSystemService } from '../../services/TrailSystemService/TrailSystemService'

import Loading from '../../components/Loading/Loading.component'
import { TrailSystemCard } from '../../components/PreviewCards/TrailSystemCard.component'
import { TrailCard } from '../../components/PreviewCards/TrailCard.component'
import TrailDataUpdateBar, {
  minutesUntilNextTrailDataUpdate,
} from '../../components/TrailDataUpdateBar/TrailDataUpdateBar.component'

const Home = (): JSX.Element => {
  const { user } = useAuth0()
  const hasViewTrailSystem = UserService.hasPermissions(user, [AppPermissions.viewTrailSystem])

  const [selectedSystemId, setSelectedSystemId] = useState<number | null>(null)
  const topRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    AppLayoutContext.setPageName('Home')
  }, [])

  // When drilling into or out of a system, reset the scroll position so the
  // user lands at the top of the newly rendered view instead of inheriting the
  // previous scroll offset. We walk up to the nearest scrollable ancestor
  // because the admin layout scrolls an inner <main> container, not the window.
  useEffect(() => {
    const node = topRef.current
    if (!node) return
    let el: HTMLElement | null = node.parentElement
    while (el) {
      const { overflowY } = window.getComputedStyle(el)
      if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
        el.scrollTop = 0
        return
      }
      el = el.parentElement
    }
    window.scrollTo(0, 0)
  }, [selectedSystemId])

  const {
    isLoading,
    isError,
    error,
    data: trailSystems,
  } = useQuery(['TrailSystemService.preview', AppStateContext.getRegion()], () => new TrailSystemService().preview(), {
    enabled: hasViewTrailSystem,
    // refetch when new trail statuses become available, like the public app (see TrailDataUpdateBar)
    refetchInterval: () => minutesUntilNextTrailDataUpdate() * 60 * 1000,
  })

  // Freeze/thaw ring is shown across all system cards if any trail in the region is freeze/thawing,
  // mirroring the public app's behavior.
  const anyFreezeThawActive = useMemo(
    () =>
      (trailSystems || []).some((system) =>
        (system.trails || []).some((trail) => (trail.status?.freezeThawStatus?.status ?? -1) > 0),
      ),
    [trailSystems],
  )

  const selectedSystem = useMemo(
    () => trailSystems?.find((system) => system.id === selectedSystemId),
    [trailSystems, selectedSystemId],
  )

  if (!hasViewTrailSystem) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h5">Welcome {user?.name}</Typography>
        <Typography sx={{ mt: 1 }}>You do not have permission to preview trail systems.</Typography>
      </Paper>
    )
  }

  if (isLoading) {
    return (
      <Box sx={{ height: '60vh', width: '100%' }}>
        <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ width: '100%', height: '100%' }}>
          <Loading sx={{ height: 'auto' }} />
          <Typography>Your Trail System Preview is loading</Typography>
        </Stack>
      </Box>
    )
  }

  if (isError) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          Trail System Preview
        </Typography>
        <Typography color="error">
          {typeof error === 'string' ? error : 'Sorry, there was a problem loading the preview.'}
        </Typography>
      </Paper>
    )
  }

  // Drill-in: a single system's trail cards.
  if (selectedSystem) {
    return (
      <Box ref={topRef} sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => setSelectedSystemId(null)}>
            All systems
          </Button>
          <Typography variant="h5">{selectedSystem.name}</Typography>
        </Stack>
        {selectedSystem.trails && selectedSystem.trails.length > 0 ? (
          <Grid container spacing={3}>
            {selectedSystem.trails.map((trail) => (
              <Grid item zero={12} sm={6} md={4} lg={3} key={trail.id}>
                <TrailCard trail={trail} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography>This trail system has no trails yet.</Typography>
        )}
        <TrailDataUpdateBar />
      </Box>
    )
  }

  // Systems overview.
  return (
    <Box ref={topRef} sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Trail System Preview
      </Typography>
      {trailSystems && trailSystems.length > 0 ? (
        <Grid container spacing={3}>
          {trailSystems.map((trailSystem) => (
            <Grid item zero={12} sm={6} lg={4} key={trailSystem.id}>
              <TrailSystemCard
                trailSystem={trailSystem}
                anyFreezeThawActive={anyFreezeThawActive}
                onSelect={setSelectedSystemId}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Typography>No trail systems found for this region.</Typography>
      )}
      <TrailDataUpdateBar />
    </Box>
  )
}
export default Home
